import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { timeRangeFilterId, virtualMachineFilterId } from '../../filters';

const title = t`Virtual Machine Packet Drops`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['vmNetRxDrops', 'vmNetTxDrops'],
    },
};

function useChartConfig() {
    const inColor = useMetricColor('serverNetIn');
    const outColor = useMetricColor('serverNetOut');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Packets Dropped`,
                    },
                },
            ],
            chartType: 'areaspline',
            stacking: 'normal',
            tooltip: BuiltInTooltip,
            showGranularity: true,
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.INTEGER,
                    units: ' Packets',
                    chartType: 'areaspline',
                    key: 'vmNetRxDrops',
                    label: t`In`,
                    color: inColor,
                    multiplier: 1,
                },
                {
                    formatter: FORMATTER_TYPES.INTEGER,
                    units: ' Packets',
                    chartType: 'areaspline',
                    key: 'vmNetTxDrops',
                    label: t`Out`,
                    color: outColor,
                    multiplier: 1,
                },
            ],
        }),
        [inColor, outColor]
    );
}

function VirtualMachinePacketDropsPanel() {
    const chartConfig = useChartConfig();
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

VirtualMachinePacketDropsPanel.propTypes = panelPropTypes;

VirtualMachinePacketDropsPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareVmPacketDropsTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.VIRTUAL_MACHINE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Packet drops trend for a selected virtual machine (VM)`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, virtualMachineFilterId],
        optional: [],
    },
})(VirtualMachinePacketDropsPanel);
